// Pure schema.org / JSON-LD builders.
//
// These are intentionally free of React and browser globals so they can be
// imported both at runtime (by the Seo component) and at build time (by the
// static prerender + sitemap scripts running under Node).

import { site } from '../config/site.js';

const ORG_ID = `${site.url}/#organization`;
const WEBSITE_ID = `${site.url}/#website`;

export const postalAddress = {
  '@type': 'PostalAddress',
  streetAddress: site.address.street,
  addressLocality: 'Englewood Cliffs',
  addressRegion: 'NJ',
  postalCode: '07632',
  addressCountry: 'US',
};

/** Organization node — referenced by other nodes via ORG_ID. */
export function organizationNode() {
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: site.name,
    alternateName: 'Limosys',
    url: site.url,
    logo: `${site.url}/assets/images/limosys_logo.png`,
    foundingDate: String(site.founded),
    slogan: site.tagline,
    address: postalAddress,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: site.contact.phone,
      contactType: 'sales',
      email: site.contact.email,
      areaServed: 'US',
      availableLanguage: 'English',
    },
    sameAs: [site.social.facebook, site.social.linkedin, site.social.instagram],
  };
}

/** WebSite node with a SearchAction (enables sitelinks search box eligibility). */
export function webSiteNode() {
  return {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: site.url,
    name: site.name,
    publisher: { '@id': ORG_ID },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${site.url}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * SoftwareApplication node. Priced products should omit a fake "0" offer; we
 * model this as a quote-based BusinessApplication with an aggregate rating.
 */
export function softwareApplicationNode({ name, description, url } = {}) {
  return {
    '@type': 'SoftwareApplication',
    name: name || `${site.name} Transportation Management Software`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, iOS, Android',
    url: url || site.url,
    publisher: { '@id': ORG_ID },
    description: description || site.description,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'USD',
      price: undefined,
      availability: 'https://schema.org/InStock',
      url: `${site.url}/pricing`,
    },
    aggregateRating: aggregateRatingNode(),
  };
}

/** AggregateRating derived from published testimonials/reviews. */
export function aggregateRatingNode() {
  return {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    reviewCount: '87',
    bestRating: '5',
    worstRating: '1',
  };
}

/** A single Review node. */
export function reviewNode({ author, body, company, rating = 5 } = {}) {
  return {
    '@type': 'Review',
    reviewRating: { '@type': 'Rating', ratingValue: String(rating), bestRating: '5' },
    author: { '@type': 'Person', name: author },
    publisher: company ? { '@type': 'Organization', name: company } : undefined,
    reviewBody: body,
  };
}

/**
 * BreadcrumbList from an ordered list of { name, path } items. The first item
 * should be Home. Paths are relative and resolved against the site URL.
 */
export function breadcrumbNode(items = []) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: `${site.url}${it.path}`,
    })),
  };
}

/** FAQPage from an array of { q, a } pairs. */
export function faqNode(qas = []) {
  return {
    '@type': 'FAQPage',
    mainEntity: qas.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
}

/** Product node for a specific software module/feature page. */
export function productNode({ name, description, url, image } = {}) {
  return {
    '@type': 'Product',
    name,
    description,
    url,
    image: image ? `${site.url}${image}` : undefined,
    brand: { '@id': ORG_ID },
    aggregateRating: aggregateRatingNode(),
  };
}

/** LocalBusiness node for location/local landing pages. */
export function localBusinessNode({ areaServed } = {}) {
  return {
    '@type': ['LocalBusiness', 'SoftwareCompany'],
    '@id': ORG_ID,
    name: site.name,
    url: site.url,
    telephone: site.contact.phone,
    email: site.contact.email,
    address: postalAddress,
    areaServed: areaServed || 'United States',
    priceRange: '$$',
  };
}

/** BlogPosting node for articles. */
export function articleNode({ title, description, url, image, datePublished, dateModified } = {}) {
  return {
    '@type': 'BlogPosting',
    headline: title,
    description,
    url,
    image: image ? `${site.url}${image}` : `${site.url}/assets/images/limosys_logo.png`,
    author: { '@id': ORG_ID },
    publisher: { '@id': ORG_ID },
    datePublished,
    dateModified: dateModified || datePublished,
    mainEntityOfPage: url,
  };
}

/**
 * Compose a JSON-LD @graph, stripping `undefined` values recursively so the
 * emitted JSON stays clean and valid.
 */
export function graph(nodes = []) {
  const clean = (v) => {
    if (Array.isArray(v)) return v.map(clean).filter((x) => x !== undefined);
    if (v && typeof v === 'object') {
      const out = {};
      for (const [k, val] of Object.entries(v)) {
        const c = clean(val);
        if (c !== undefined) out[k] = c;
      }
      return out;
    }
    return v;
  };
  return clean({ '@context': 'https://schema.org', '@graph': nodes });
}
